import { faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Product } from '@shared/components/ui/Product';
import { StepperInput } from '@shared/components/ui/StepperInput';
import type { ProductType, SelectedProduct } from '@shared/components/ui/ProductSelector';
import { Package } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProductSelectorModal from '../modals/ProductSelectorModal';

interface ProductFormProps {
  /** Productos disponibles para seleccionar */
  products: ProductType[];
  /** Productos inicialmente seleccionados */
  initialSelectedProducts?: SelectedProduct[];
  /** Callback cuando cambian los productos seleccionados */
  onProductsChange?: (products: SelectedProduct[]) => void;
  /** Si está en estado de carga */
  isLoading?: boolean;
  /** Si está deshabilitado */
  disabled?: boolean;
  /** Título de la sección */
  title?: string;
  /** Descripción de la sección */
  description?: string;
  /** Permite selección múltiple */
  allowMultiple?: boolean;
  /** Cantidad máxima por producto */
  maxQuantity?: number;
}

const ProductForm = memo<ProductFormProps>(
  ({
    products,
    initialSelectedProducts = [],
    onProductsChange,
    isLoading = false,
    disabled = false,
    title,
    description,
    allowMultiple = true,
    maxQuantity = 999999999,
  }) => {
    const { t } = useTranslation('merchandiseV3');
    const productModalRef = useRef<any>(null);
    const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>(initialSelectedProducts);

    // Efecto para sincronizar estado interno cuando cambien initialSelectedProducts
    useEffect(() => {
      if (initialSelectedProducts.length > 0) {
        setSelectedProducts(initialSelectedProducts);
      }
    }, [initialSelectedProducts]);

    // Handler optimizado para cambios en productos seleccionados
    const handleProductsSelected = useCallback(
      (products: SelectedProduct[]) => {
        setSelectedProducts(products);
        onProductsChange?.(products);
      },
      [onProductsChange],
    );

    // Handler para abrir la modal
    const handleOpenSelector = useCallback(() => {
      productModalRef.current?.openSelector();
    }, []);

    // Handler para remover un producto específico
    const handleRemoveProduct = useCallback(
      (productId: string | number) => {
        const updatedProducts = selectedProducts.filter((product) => product.id !== productId);
        setSelectedProducts(updatedProducts);
        onProductsChange?.(updatedProducts);
      },
      [selectedProducts, onProductsChange],
    );

    const handleQuantityChange = useCallback(
      (productId: string | number, quantity: number) => {
        const updatedProducts = selectedProducts.map((product) =>
          product.id === productId ? { ...product, quantity } : product,
        );
        setSelectedProducts(updatedProducts);
        onProductsChange?.(updatedProducts);
      },
      [selectedProducts, onProductsChange],
    );

    // Props memoizadas para ProductModal
    const productModalProps = useMemo(
      () => ({
        ref: productModalRef,
        products,
        onProductsSelected: handleProductsSelected,
        initialSelectedProducts: selectedProducts,
        isLoading,
        disabled,
        allowMultiple,
        maxQuantity,
        buttonText: t('form.products.select_products', 'Seleccionar productos'),
        loadingText: t('form.products.loading_products', 'Cargando productos...'),
        title: t('modal.product_selector.title', 'Seleccionar productos'),
      }),
      [products, handleProductsSelected, selectedProducts, isLoading, disabled, allowMultiple, maxQuantity, t],
    );

    // Usar traducciones o valores por defecto
    const sectionTitle = title || t('form.products.title', 'Productos');
    const sectionDescription = description || t('form.products.description', 'Selecciona los productos de la entrada');

    return (
      <div className="grid grid-cols-1 lg:grid-cols-6 gap-8">
        {/* Columna izquierda - Título de la sección */}
        <div className="lg:col-span-2">
          <div className="sticky top-6 space-y-4">
            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-foreground">{sectionTitle}</h2>
              <p className="text-sm text-muted-foreground">{sectionDescription}</p>
            </div>
          </div>
        </div>

        {/* Columna derecha - Contenido del ProductManager */}
        <div className="lg:col-span-4">
          <div className="space-y-4">
            {/* Lista de productos seleccionados */}
            {selectedProducts.length === 0 ? (
              <div className="border-2 border-dashed border-card-border rounded-lg p-8 text-center space-y-4">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <h3 className="font-medium">
                  {t('form.products.no_products_selected', 'No hay productos seleccionados')}
                </h3>
                <p className="text-sm text-muted-foreground">{sectionDescription}</p>
                <div className="flex justify-center">
                  <ProductSelectorModal {...productModalProps} />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-muted-foreground">
                    {t(
                      'form.products.products_selected',
                      { count: selectedProducts.length },
                      `${selectedProducts.length} productos seleccionados`,
                    )}
                  </p>
                  <div className="flex justify-center">
                    <ProductSelectorModal {...productModalProps} />
                  </div>
                </div>

                <div className="">
                  {selectedProducts.map((product) => (
                    <div
                      key={product.id}
                      className="flex items-center justify-between gap-4 py-3 border-b border-card-border last:border-b-0 hover:bg-accent/50 transition-colors group"
                    >
                      {/* Producto a la izquierda */}
                      <div className="flex-grow min-w-0">
                        <Product
                          id={product.id as number}
                          name={product.name}
                          src={product.image ?? ''}
                          code={product.code ?? ''}
                          showImageModal={true}
                        />
                      </div>

                      {/* Controles a la derecha */}
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <StepperInput
                          value={product.quantity}
                          onChange={(value) => handleQuantityChange(product.id, value)}
                          min={1}
                          max={maxQuantity}
                          disabled={disabled}
                          data-testid={`product-quantity-${product.id}`}
                          className="w-40"
                        />

                        <button
                          type="button"
                          onClick={() => handleRemoveProduct(product.id)}
                          disabled={disabled}
                          className="p-2 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title={t('form.products.remove_product', 'Eliminar producto')}
                        >
                          <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

ProductForm.displayName = 'ProductForm';

export { ProductForm };
export default ProductForm;
